import React, { useEffect, useState } from "react";
import { Spin, Table, Tag, Badge, Alert } from "antd";
import { getNursingDashboard } from "../../apis/roleDashboard";

interface Transfer {
  id: number;
  visitId: number;
  patientName: string;
  fromRoom: string;
  toRoom: string;
  time: string;
  reason: string | null;
}

interface DischargeAlert {
  id: number;
  patientName: string;
  room: string;
  admittedOn: string | null;
  days: number | null;
  status: string | null;
}

interface LongStay {
  id: number;
  patientName: string;
  room: string;
  days: number;
}

interface NursingData {
  transfersToday: Transfer[];
  dischargeAlerts: DischargeAlert[];
  longStay: LongStay[];
  activeIpd: number;
  transfersCount: number;
}

const NursingDashboard: React.FC = () => {
  const [data, setData] = useState<NursingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getNursingDashboard()
      .then((res: any) => setData(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center mt-16"><Spin size="large" /></div>;
  if (!data) return <div className="text-gray-400 mt-8 text-center">Failed to load dashboard.</div>;

  const transferColumns = [
    { title: "Time", dataIndex: "time", key: "time", width: 70 },
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    {
      title: "Movement",
      key: "movement",
      render: (_: any, row: Transfer) => (
        <span className="text-sm">
          <span className="text-gray-600">{row.fromRoom}</span>
          <span className="mx-1 text-gray-400">→</span>
          <span className="font-medium text-blue-700">{row.toRoom}</span>
        </span>
      ),
    },
    { title: "Reason", dataIndex: "reason", key: "reason", render: (v: string) => v ?? "—" },
  ];

  const alertColumns = [
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    { title: "Room", dataIndex: "room", key: "room" },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 70,
      render: (v: number) => <Badge count={v} color={v > 7 ? "red" : "orange"} />,
    },
    { title: "Admitted", dataIndex: "admittedOn", key: "admittedOn" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => v ? <Tag color="orange">{v}</Tag> : null,
    },
  ];

  const longStayColumns = [
    { title: "Patient", dataIndex: "patientName", key: "patientName" },
    { title: "Room", dataIndex: "room", key: "room" },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 80,
      render: (v: number) => <Tag color="red">{v} days</Tag>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Active IPD</p>
          <p className="text-3xl font-bold text-blue-600">{data.activeIpd}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Transfers Today</p>
          <p className="text-3xl font-bold text-amber-600">{data.transfersCount}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Discharge Alerts</p>
          <p className="text-3xl font-bold text-orange-500">{data.dischargeAlerts.length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border flex-1 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Long Stay (&gt;7d)</p>
          <p className="text-3xl font-bold text-red-500">{data.longStay.length}</p>
        </div>
      </div>

      {/* Discharge alerts */}
      {data.dischargeAlerts.length > 0 && (
        <div className="bg-white rounded-lg p-4 shadow-sm border border-orange-200">
          <h3 className="font-semibold text-orange-700 mb-3">
            Discharge Initiated — Needs Clearance
          </h3>
          <Table
            dataSource={data.dischargeAlerts}
            columns={alertColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        </div>
      )}

      {/* Long stay alerts */}
      {data.longStay.length > 0 && (
        <div className="bg-white rounded-lg p-4 shadow-sm border border-red-200">
          <h3 className="font-semibold text-red-700 mb-2">Long-stay Patients (&gt; 7 days)</h3>
          <Alert
            type="warning"
            message="These patients have been admitted for over 7 days with no discharge initiated."
            className="mb-3"
            showIcon
          />
          <Table
            dataSource={data.longStay}
            columns={longStayColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        </div>
      )}

      {/* Room transfers today */}
      <div className="bg-white rounded-lg p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-700 mb-3">Room Transfers Today</h3>
        <Table
          dataSource={data.transfersToday}
          columns={transferColumns}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: "No room transfers today" }}
        />
      </div>
    </div>
  );
};

export default NursingDashboard;
